import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { github } from '../lib/api';
import { useToast } from '../components/Toast';

export function SettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAddToken, setShowAddToken] = useState(false);
  const [newTokenValue, setNewTokenValue] = useState('');
  const [newTokenLabel, setNewTokenLabel] = useState('');

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['github-status'],
    queryFn: github.status,
  });

  const ghStatus = statusData?.data;
  const tokens = ghStatus?.tokens || [];

  const connectMutation = useMutation({
    mutationFn: ({ token, label }: { token: string; label: string }) => github.connect(token, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-status'] });
      queryClient.invalidateQueries({ queryKey: ['github-repos'] });
      toast('GitHub token added!', 'success');
      setShowAddToken(false);
      setNewTokenValue('');
      setNewTokenLabel('');
    },
    onError: (err: any) => toast(err.message || 'Failed to connect GitHub', 'error'),
  });

  const disconnectMutation = useMutation({
    mutationFn: (tokenId: string) => github.disconnect(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-status'] });
      queryClient.invalidateQueries({ queryKey: ['github-repos'] });
      toast('GitHub token removed', 'success');
    },
    onError: () => toast('Failed to remove GitHub token', 'error'),
  });

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="mt-1 text-sm text-gray-400">Manage your account and preferences</p>
      </div>

      <div className="space-y-6">
        {/* Account */}
        <section className="rounded-xl border border-gray-800 bg-surface-850 p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">Account</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-200">{user?.email || '—'}</p>
            </div>
            <div>
              <label className="block text-xs text-gray-500">User ID</label>
              <p className="mt-1 font-mono text-xs text-gray-400">{user?.sub || '—'}</p>
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="rounded-xl border border-gray-800 bg-surface-850 p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">Integrations</h2>
          <div className="space-y-3">
            <div className="rounded-lg bg-surface-900 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-200">GitHub</p>
                    <p className="text-xs text-gray-500">
                      {tokens.length === 0 ? 'Connect your repositories' : `${tokens.length} token${tokens.length === 1 ? '' : 's'} connected`}
                    </p>
                  </div>
                </div>
                {statusLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                ) : (
                  <button
                    onClick={() => setShowAddToken(true)}
                    className="rounded-lg bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-700"
                  >
                    Add Token
                  </button>
                )}
              </div>

              {/* Token list */}
              {tokens.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-gray-800 pt-3">
                  {tokens.map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg bg-surface-850 px-3 py-2">
                      <div className="flex items-center gap-2">
                        {t.avatarUrl && (
                          <img src={t.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-200">{t.label}</p>
                          <p className="text-[10px] text-gray-500">@{t.username}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => disconnectMutation.mutate(t.id)}
                        disabled={disconnectMutation.isPending}
                        className="rounded-lg px-2 py-1 text-[10px] font-medium text-red-400 hover:bg-red-500/10"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add token form */}
              {showAddToken && (
                <div className="mt-3 space-y-2 border-t border-gray-800 pt-3">
                  <input
                    type="text"
                    value={newTokenLabel}
                    onChange={(e) => setNewTokenLabel(e.target.value)}
                    placeholder="Label (e.g. Acme Corp, Personal)"
                    className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                    autoFocus
                  />
                  <input
                    type="password"
                    value={newTokenValue}
                    onChange={(e) => setNewTokenValue(e.target.value)}
                    placeholder="github_pat_xxxxxxxxxxxx"
                    className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                  />
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>
                      Create a{' '}
                      <a
                        href="https://github.com/settings/personal-access-tokens/new"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-400 hover:underline"
                      >
                        fine-grained personal access token
                      </a>
                      {' '}scoped to your org/repo(s) with these permissions:
                    </p>
                    <ul className="ml-3 list-disc space-y-0.5 text-gray-500">
                      <li><span className="text-gray-400">Contents</span> — Read and write</li>
                      <li><span className="text-gray-400">Pull requests</span> — Read and write</li>
                      <li><span className="text-gray-400">Actions</span> — Read-only</li>
                      <li><span className="text-gray-400">Commit statuses</span> — Read-only</li>
                    </ul>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => connectMutation.mutate({ token: newTokenValue, label: newTokenLabel })}
                      disabled={!newTokenValue.trim() || connectMutation.isPending}
                      className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-500 disabled:opacity-40"
                    >
                      {connectMutation.isPending ? 'Connecting...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setShowAddToken(false); setNewTokenValue(''); setNewTokenLabel(''); }}
                      className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Preferences */}
        <section className="rounded-xl border border-gray-800 bg-surface-850 p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">Preferences</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-200">Dark Mode</p>
                <p className="text-xs text-gray-500">Always dark — as it should be</p>
              </div>
              <div className="rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white">On</div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-200">Real-time Updates</p>
                <p className="text-xs text-gray-500">Live card movements via WebSocket</p>
              </div>
              <div className="rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white">On</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
